import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { MenuModel } from '../../models/menus/menu.model';
import { AddOrEditMenuComponent } from './add-or-edit-menu/add-or-edit-menu.component';
import { DeleteMenuComponent } from './delete-menu/delete-menu.component';
import { TranslateService } from "@ngx-translate/core";
import { AssignMenuToVenuesComponent } from "./assign-menu-to-venues/assign-menu-to-venues.component";
import { AuthenticationService } from "../../../share-modul/services/authentication/authentication.service";
import { Subject, takeUntil } from "rxjs";
import { MenuService } from "../../services/menus/menu.service";
import { Router } from "@angular/router";

@Component({
  selector: 'app-menus',
  templateUrl: './menus.component.html',
  styleUrls: ['./menus.component.scss']
})
export class MenusComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public listAllMenus: Array<MenuModel> = new Array<MenuModel>();
  public spinner: boolean;
  public companyId: string;
  public items: { [name: string]: MenuItem[] } = {};
  //#endregion

  //#region Constructor
  constructor(
    public dialogService: DialogService,
    public router: Router,
    private messageService: MessageService,
    private translate: TranslateService,
    private menuService: MenuService,
    private authenticationService: AuthenticationService
  ) {
    this.translate.stream('HEADER.APP_LANGUAGE').subscribe(() => {
      this.addFunctionToAllMenus();
    });
  }

  public ngOnInit(): void {
    this.companyId = this.authenticationService.getCompanyId();
    this.loadAllMenus();
  }
  //#endregion

  //#region Public Methods
  public loadAllMenus(): void {
    this.listAllMenus = new Array<MenuModel>();
    this.spinner = true;
    this.menuService.listAllMenus(this.companyId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          response.forEach((menu: MenuModel) => {
            let currentMenu = new MenuModel();
            currentMenu.id = menu.id;
            currentMenu.name = menu.name;
            currentMenu.createDate = menu.createDate;
            currentMenu.venues = menu.venues;
            currentMenu.venueMenuCategories = menu.venueMenuCategories.map(x => x.name);
            this.listAllMenus.push(currentMenu);
          });
          this.addFunctionToAllMenus();
          this.spinner = false;
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  public createMenu(): void {
    const ref = this.dialogService.open(AddOrEditMenuComponent, {
      data: {
        companyId: this.companyId
      },
      header: this.translate.instant('MENU.CREATE_MENU'),
      styleClass: 'dialog_menus , ml_200, '
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadAllMenus();
        this.messageService.add({severity: 'success', summary: this.translate.instant("MENU.MENU_ADDED"), life: 3000});
      }
    });
  }

  public editMenu(id: string): void {
    const ref = this.dialogService.open(AddOrEditMenuComponent, {
      data: {
        id: id,
        companyId: this.companyId
      },
      header: this.translate.instant("MENU.EDIT_MENU"),
      styleClass: 'dialog_menus , ml_200, '
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadAllMenus();
        this.messageService.add({severity: 'success', summary: this.translate.instant("MENU.MENU_EDITED"), life: 3000});
      }
    });
  }

  public deleteMenu(id: string): void {
    const ref = this.dialogService.open(DeleteMenuComponent, {
      data: {
        id: id
      },
      header: this.translate.instant("MENU.DELETE_MENU"),
      styleClass: 'dialog_long , ml_200, mb_15'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadAllMenus();
        this.messageService.add({severity: 'success', summary: this.translate.instant("MENU.MENU_DELETED"), life: 3000});
      }
    });
  }

  public assignVenues(menu: MenuModel): void {
    const ref = this.dialogService.open(AssignMenuToVenuesComponent, {
      data: {
        id: menu.id,
        venues: menu.venues
      },
      header: this.translate.instant('EMPLOYEES.ASSIGN_VENUES').toUpperCase(),
      width: '275px',
      styleClass: 'ml_200, mb_15'
    });
    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadAllMenus();
        this.messageService.add({ severity: 'success', summary: this.translate.instant("MENU.ASSIGNED_TO_VENUES"), life: 3000 });
      }
    });
  }

  public addFunctionToAllMenus(): void {
    this.listAllMenus.forEach(menu => {
        this.items[menu.id] = [{
          label: this.translate.instant('EDIT'),
          command: () => {
            this.editMenu(menu.id);
          }
        },
        {
          label: this.translate.instant('EMPLOYEES.ASSIGN_VENUES'),
          command: () => {
            this.assignVenues(menu);
          }
        },
        {
          label: this.translate.instant('DELETE'),
          command: () => {
            this.deleteMenu(menu.id);
          }
        }];
    });
  }

  public navigateToDetails(id: string): void {
    this.router.navigate(['/menus', id, 'details']);
  }
  //#endregion
}
